<template>
  <v-autocomplete
    :id="id"
    ref="agAutocomplete"
    v-model="localValue"
    v-model:search="searchTerm"
    menu-icon="mdi-chevron-down"
    hide-details="auto"
    variant="solo"
    autocomplete="off"
    autofill="off"
    clear-icon="mdi-close"
    class="ag-autocomplete"
    persistent-hint
    :clearable="clearable"
    :disabled="disabled"
    :error-messages="errorMessages"
    :filter-keys="filterKeys"
    :hint="hint"
    :item-title="itemTitle"
    :item-value="itemValue"
    :items="items"
    :label="localLabel"
    :loading="loading || internalLoading"
    :menu-props="{ offset: 8, maxWidth: maxWidth }"
    :multiple="multiple"
    :placeholder="placeholder"
    :return-object="returnObject"
    :rules="rules"
    :data-message-color="messageColor"
    :no-filter="!!serverSideFn"
    @click:clear="clear"
    @update:search="onSearch"
    @update:model-value="onUpdateModelValue"
  >
    <template v-if="labelDataQa" #label>
      <span :data-qa="normalizeQa(labelDataQa)">{{ localLabel }}</span>
    </template>

    <template #selection="{ index }">
      <span v-if="index === 0">{{ selectionTitle }}{{ searchTerm ? ' - ' : '' }}</span>
    </template>

    <template #append-item>
      <div
        v-intersect="{
          handler: onIntersect,
          options: {
            threshold: [0, 0.5, 1.0],
          },
        }"
      />
    </template>

    <template #no-data>
      <slot name="no-data">
        <AGAutocompleteListItem title="Não há dados disponíveis" disable is-group />
      </slot>
    </template>

    <template v-if="hasSelectAll && multiple && items.length" #prepend-item>
      <AGAutocompleteListItem
        title="Selecionar tudo"
        can-select
        is-group
        :active="allItemsSelected"
        :disable="selectAllDisabled"
        :message="groupMessage"
        @click="toggleAll()"
      />
    </template>

    <template #item="{ item, index, props }">
      <AGAutocompleteListItem
        v-if="item.raw.isGroup"
        v-bind="hasSelectGroup ? { ...props, onClick: () => toggleAllGroup(item.raw) } : undefined"
        :id="`grupo-${index}`"
        :key="`group-${index}`"
        is-group
        :title="item.title"
        :value="index"
        :active="allItemsSelected || isGroupSelected(item.raw)"
        :can-select="hasSelectGroup && multiple"
        :disable="groupDisabled(item.raw)"
        :disable-message="item.raw.disableMessage || groupMessage"
      />

      <AGAutocompleteListItem
        v-else
        v-bind="props"
        :id="`item-${index}`"
        :key="`item-${index}`"
        :value="index"
        :active="allItemsSelected || isItemSelected(item.raw)"
        :can-select="multiple"
        :disable="!!itemDisabled(item.raw)"
        :disable-message="item.raw.disableMessage || messageReachLimit"
        :has-group="hasGroup(item.raw)"
        :subtitle="getItemSubtitle(item.raw)"
        @click="onToggleItem(item.raw)"
      />
    </template>
  </v-autocomplete>
</template>

<script>
import AGAutocompleteListItem from '@/components/common/AGAutocompleteListItem.vue';
import { MESSAGE_COLOR } from '@/constants/ConstantMessageColor';
import { cloneDeep, debounce, isEqual, uniqueId } from 'lodash';

// Isto é um recurso técnico avançado
// Quando é selecionado apenas 1 item e depois pesquisado via server-side ele mostrava undefined o item
const localItems = [];

export default {
  components: { AGAutocompleteListItem },
  emits: ['update:modelValue', 'click:clear', 'update:search'],
  props: {
    modelValue: {
      type: [Boolean, String, Number, Array, Object],
      default: '',
    },
    id: {
      type: String,
      default: uniqueId('agAutocomplete'),
    },
    label: {
      type: String,
      default: '',
    },
    labelDataQa: {
      type: String,
      default: undefined,
    },
    hint: {
      type: String,
      default: '',
    },
    errorMessages: {
      type: String,
      default: '',
    },
    placeholder: {
      type: [String],
      default: 'Selecione uma opção',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    returnObject: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hasSelectAll: {
      type: Boolean,
      default: false,
    },
    hasSelectGroup: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    limit: {
      type: Number,
      default: -1,
    },
    itemTitle: {
      type: [String, Function],
      default: 'title',
    },
    itemSubtitle: {
      type: [String, Function],
      default: 'subtitle',
    },
    itemValue: {
      type: [String, Function],
      default: 'value',
    },
    itemGroupId: {
      type: [String, Function],
      default: 'groupId',
    },
    /**
     * Enviar lista de objetos no seguinte formato:
     * item {
     *  id: any,
     *  title: any,
     *  subtitle: any | undefined,
     *  value: any,
     *  isGroup: boolean | undefined
     *  groupId: any | undefined,
     *  disabled: boolean | undefined,
     *  disableMessage: string | undefined
     * }
     */
    items: {
      type: Array,
      default: () => [],
    },
    rules: {
      type: Array,
      default: () => [],
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    messageColor: {
      type: String,
      default: MESSAGE_COLOR.DEFAULT,
      validator: (val) => Object.values(MESSAGE_COLOR).includes(val),
    },
    pagination: {
      type: Object,
      default: undefined,
    },
    serverItemsLength: {
      type: Number,
      default: -1,
    },
    serverSideFn: {
      type: Function,
      default: undefined,
    },
    applySearchCriteria: {
      type: Boolean,
      default: false,
    },
    filterKeys: {
      type: Array,
      default: () => ['title'],
    },
    emitClickClear: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      maxWidth: 350,
      panelModel: [],
      searchTerm: '',
      internalLoading: false,
    };
  },

  computed: {
    localValue: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', val);
      },
    },

    itemList() {
      return this.items.filter((item) => !item.isGroup);
    },

    itemValueList() {
      return this.itemList.map((item) => this.getItemValue(item));
    },

    groupMessage() {
      return this.hasLimit ? (this.limitReached ? this.messageReachLimit : this.messageRemainingLimit) : undefined;
    },

    quantitySelectionRemaining() {
      if (!this.multiple) return 1;
      return this.limit - this.localValue.length;
    },

    limitReached() {
      return this.limit === -1 ? false : this.localValue.length === this.limit;
    },

    totalItems() {
      return this.itemList.length;
    },

    allItemsSelected() {
      if (this.serverSideFn) return false;
      if (!this.multiple) return false;

      return this.localValue.length === this.totalItems;
    },

    hasLimit() {
      return this.limit !== -1;
    },

    firstItemTitle() {
      const currentValue = this.multiple ? this.localValue[0] : this.localValue;

      if (this.returnObject) return this.getItemTitle(currentValue);

      const searchFn = (i) => this.getItemValue(i) === this.getItemValue(currentValue);
      const item = this.items.find(searchFn) || localItems.find(searchFn);

      return item ? this.getItemTitle(item)?.toString() : undefined;
    },

    selectionTitle() {
      if (!this.multiple) return this.firstItemTitle ?? '';

      return this.localValue.length > 0
        ? this.localValue.length === 1
          ? `${this.firstItemTitle}`
          : `${this.localValue.length} selecionados`
        : '';
    },

    messageReachLimit() {
      return this.hasLimit ? `Você atingiu o limite de ${this.limit} seleções` : undefined;
    },

    messageRemainingLimit() {
      return `Você pode selecionar só mais ${this.quantitySelectionRemaining} ${
        this.quantitySelectionRemaining === 1 ? 'item' : 'itens'
      }`;
    },

    selectAllDisabled() {
      if (!this.hasLimit) return false;

      return this.itemList.length > this.limit;
    },

    campoObrigatorio() {
      return this.rules.some((rule) => rule?.name?.toLowerCase().includes('required'));
    },

    localLabel() {
      return `${this.label}${this.campoObrigatorio ? ' *' : ''}`;
    },
  },

  created() {
    window.addEventListener('resize', this.handleResize);
  },

  mounted() {
    setTimeout(() => this.handleResize(), 100);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    onIntersect(_, entries) {
      if (!entries[0].intersectionRatio >= 0.5) return;
      if (!this.serverSideFn) return;
      if (this.items.length >= this.serverItemsLength) return;

      this.onServerSideFn({ page: this.pagination.page + 1 });
    },
    handleResize() {
      if (!this.$refs.agAutocomplete) return;

      this.maxWidth = this.$refs.agAutocomplete.$el.clientWidth - 1;
    },

    getItemTitle(item) {
      if (!item) return;
      if (typeof this.itemTitle === 'function') return this.itemTitle(item);

      return item[this.itemTitle];
    },

    getItemSubtitle(item) {
      if (typeof this.itemSubtitle === 'function') return this.itemSubtitle(item);

      return item[this.itemSubtitle];
    },

    getItemValue(item) {
      if (['number', 'string', 'boolean'].includes(typeof item)) return item;
      if (!item) return;

      if (this.returnObject) return item;

      if (typeof this.itemValue === 'function') return this.itemValue(item);

      return item[this.itemValue];
    },

    getItemGroupId(item) {
      if (typeof this.itemGroupId === 'function') return this.itemGroupId(item);

      return item[this.itemGroupId];
    },

    getItemIndex(item) {
      return this.localValue.findIndex((value) => {
        if (this.returnObject) return isEqual(item, value);

        return this.getItemValue(value) === this.getItemValue(item);
      });
    },

    getAllItemsGroup(group) {
      return this.items.filter((item) => {
        const itemGroupId = this.getItemGroupId(item);

        return itemGroupId && itemGroupId === group.id;
      });
    },

    unselectedItemsGroup(group) {
      return this.items.filter((item) => {
        const itemGroupId = this.getItemGroupId(item);

        return itemGroupId && itemGroupId === group.id && !this.isItemSelected(item);
      }).length;
    },

    isItemSelected(item) {
      if (!this.multiple) return this.getItemValue(this.localValue) === this.getItemValue(item);

      return this.getItemIndex(item) !== -1;
    },

    hasGroup(item) {
      const itemGroupId = this.getItemGroupId(item);
      if (!itemGroupId) return false;
      return !!this.items.filter((i) => i.isGroup && itemGroupId === i.id);
    },

    selectItem(item) {
      const value = this.getItemValue(item);

      if (!value) return;

      if (!this.multiple) {
        this.localValue = value;
      } else {
        this.localValue.push(value);
      }
    },

    unselectItem(item) {
      if (!this.multiple) {
        this.localValue = null;
      } else {
        const itemIndex = this.getItemIndex(item);
        this.localValue.splice(itemIndex, 1);
      }
    },

    selectGroupItems(group) {
      this.getAllItemsGroup(group)
        .filter((item) => !this.isItemSelected(item))
        .forEach((item) => {
          this.selectItem(item);
        });
    },

    unselectGroupItems(group) {
      this.getAllItemsGroup(group)
        .filter((item) => this.isItemSelected(item))
        .forEach((item) => {
          this.unselectItem(item);
        });
    },

    isGroupSelected(groupItem) {
      if (!this.multiple) return false;

      const groupItemsValues = new Set(this.getAllItemsGroup(groupItem).map((item) => this.getItemValue(item)));

      const selectedItemsCount = this.localValue.filter((item) => {
        if (this.returnObject) {
          return this.getItemGroupId(item) === groupItem.id;
        }

        return groupItemsValues.has(this.getItemValue(item));
      }).length;

      return selectedItemsCount === groupItemsValues.size;
    },

    onToggleItem(item) {
      if (!this.serverSideFn) return;

      localItems.push(item);
    },

    toggleAllGroup(group) {
      if (!this.hasSelectGroup || !this.multiple || this.groupDisabled(group)) return;

      this.isGroupSelected(group) ? this.unselectGroupItems(group) : this.selectGroupItems(group);
    },

    toggleAll() {
      if (this.selectAllDisabled) return;

      this.localValue = this.allItemsSelected ? [] : [...this.itemValueList];
    },

    itemDisabled(item) {
      if (item.disabled) return true;
      if (!this.hasLimit) return false;

      return this.limitReached && !this.isItemSelected(item);
    },

    groupDisabled(group) {
      if (group.disabled) return true;
      if (!this.hasLimit) return false;

      if (this.isGroupSelected(group)) return false;

      const totalSelectedItems = this.localValue.length;
      const totalUnselectedGroupItems = this.unselectedItemsGroup(group);

      return totalSelectedItems + totalUnselectedGroupItems > this.limit;
    },

    clear() {
      this.localValue = this.multiple ? [] : null;
      if (this.emitClickClear) {
        this.$emit('click:clear');
      }
    },

    debounceServerSideSearch: debounce(async function () {
      this.onServerSideFn({});
    }, 500),

    onUpdateModelValue() {
      if (this.serverSideFn) {
        this.debounceServerSideSearch();
      }
    },

    onSearch(val) {
      if (this.serverSideFn) {
        this.debounceServerSideSearch();
      } else {
        this.$emit('update:search', val);
      }
    },

    onServerSideFn({ page = 1 }) {
      try {
        let searchTerm = cloneDeep(this.searchTerm);

        if (searchTerm && this.applySearchCriteria) {
          searchTerm = searchTerm + '$lk$$nml$';
        }

        this.internalLoading = true;
        this.serverSideFn({ page, search: searchTerm });
      } finally {
        this.internalLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/input.scss';
@import '@/styles/select.scss';

.ag-autocomplete__item {
  &[has-group='true'] {
    padding-left: $spacing-07 !important;
  }
}
</style>
